import { getConsent, getGaClientId, getSessionId, getUserId, getVisitorId } from './context.js';
import { getDataLayer } from './dataLayer.js';
import { getExposedExperiments } from './experiments.js';
import { newId } from './ids.js';
import { getConfig } from './init.js';
import { emitTrackingEvent } from './inspector.js';
import { renewSession } from './session.js';
import type { TrackingEvents, TrackMeta } from './types.js';

interface EventEntry {
  /** Stable per-event id (client-generated) so retries + at-least-once delivery dedupe downstream. */
  event_id: string;
  name: string;
  params: Record<string, unknown>;
  meta: TrackMeta;
  timestamp: number;
}

interface EventPayload {
  /** Envelope contract version; bump when the payload/param conventions change so old/new rows are separable. */
  schema_version: number;
  consent: boolean;
  ga_client_id: string | null;
  user_id: string | null;
  /** Durable per browser; the key anonymous and account rows are joined on. */
  visitor_id: string | null;
  /** One visit, rolls over after 30 minutes idle. Not a substitute for visitor_id. */
  session_id: string | null;
  /** Only the arms this visitor was exposed to, never the full flag bag. */
  experiments: Record<string, string>;
  release: string;
  environment: string;
  events: EventEntry[];
}

const SCHEMA_VERSION = 2;

const MAX_QUEUE_SIZE = 50;
const RETRY_DELAY = 3000;

let preInitQueue: EventEntry[] = [];
const retryTimers = new Set<ReturnType<typeof setTimeout>>();

function buildPayload(entry: EventEntry): EventPayload {
  const config = getConfig();
  return {
    schema_version: SCHEMA_VERSION,
    consent: getConsent(),
    ga_client_id: getGaClientId(),
    user_id: getUserId(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    experiments: getExposedExperiments(),
    release: config?.release ?? '',
    environment: config?.environment ?? '',
    events: [entry],
  };
}

function post(url: string, payload: EventPayload) {
  try {
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    });
  } catch (err) {
    // fetch() can throw synchronously (e.g. keepalive quota exceeded); normalize to a rejection.
    return Promise.reject(err);
  }
}

function pushToDataLayer(entry: EventEntry) {
  try {
    const config = getConfig();
    const adapter = config?.dataLayerAdapter;

    let mapped: Record<string, unknown> | null;
    if (adapter) {
      mapped = adapter({
        name: entry.name,
        params: entry.params,
        meta: entry.meta,
        timestamp: entry.timestamp,
        config: { release: config.release, platform: config.platform },
        userId: getUserId(),
        sessionId: getSessionId(),
        gaClientId: getGaClientId(),
      });
      // A mapper, too, may name a field it has no value for.
      mapped = mapped && withValues(mapped);
    } else {
      mapped = withValues({ event: entry.name, ...entry.meta, ...entry.params });
    }

    // Feed the inspector before the dataLayer, so events are visible even
    // pre-init (no dataLayer yet) or when a mapper suppresses the push
    // (mapped === null). Copy `mapped` so GTM's post-push mutation of the
    // pushed object (e.g. `gtm.uniqueEventId`) doesn't leak into the inspector.
    emitTrackingEvent({
      event_id: entry.event_id,
      name: entry.name,
      params: entry.params,
      meta: entry.meta,
      timestamp: entry.timestamp,
      mapped: mapped ? { ...mapped } : null,
    });

    const dl = getDataLayer();
    if (dl && mapped) {
      dl.push(mapped);
    }
  } catch (err) {
    getConfig()?.logger?.warn('[tracking] pushToDataLayer failed', { error: String(err) });
  }
}

/** Sends one event as its own request, right away. No batching: an event either reaches
 * /collect on this call or gets one retry, never held back waiting for siblings. */
function sendEvent(entry: EventEntry) {
  const config = getConfig();
  if (!config) {
    return;
  }

  const payload = buildPayload(entry);

  if (config.debug) {
    config.logger?.debug(`[tracking] ${entry.name}`, {
      params: entry.params,
      meta: entry.meta,
      user_id: payload.user_id,
      visitor_id: payload.visitor_id,
      session_id: payload.session_id,
      ga_client_id: payload.ga_client_id,
      consent: payload.consent,
    });
  }

  // undefined = collect disabled; '' = same-origin relative (posts to /collect).
  if (config.gatewayUrl === undefined) {
    return;
  }

  const scheduleRetry = () => {
    const timer = setTimeout(() => {
      retryTimers.delete(timer);
      // Re-check config: gatewayUrl/consent may have changed since the first attempt.
      const retryConfig = getConfig();
      if (!retryConfig || retryConfig.gatewayUrl === undefined) {
        return;
      }
      const retryPayload = buildPayload(entry);
      post(`${retryConfig.gatewayUrl}/collect`, retryPayload).catch(() => {
        // second failure: drop silently, tracking must never break the app.
      });
    }, RETRY_DELAY);
    retryTimers.add(timer);
  };

  // fetch only rejects on network errors; a non-2xx (e.g. 503 during a BFF
  // rollout) still resolves, so retry those too, not just rejections.
  post(`${config.gatewayUrl}/collect`, payload).then(
    (res) => {
      if (!res.ok) {
        scheduleRetry();
      }
    },
    () => scheduleRetry(),
  );
}

/** Use for events fired right before page navigation (e.g. login redirect, logout).
 * `track()` already sends immediately; this just fills in `page`. */
export function trackBeforeNav<E extends keyof TrackingEvents>(event: E, properties: TrackingEvents[E], meta: TrackMeta = {}) {
  track(event, properties, { page: window.location.pathname, ...meta });
}

/**
 * Lets a caller write `{ method }` for an optional field instead of spreading it in conditionally. JSON
 * omits empty keys on the wire anyway; this makes the dataLayer and the inspector agree with it.
 */
function withValues<T extends Record<string, unknown>>(fields: T): T {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as T;
}

export function track<E extends keyof TrackingEvents>(event: E, properties: TrackingEvents[E], meta: TrackMeta = {}) {
  // A session id is storage on the device, so it waits for consent exactly like the visitor id does.
  if (getConsent()) {
    renewSession();
  }

  const entry: EventEntry = {
    event_id: newId(),
    name: String(event),
    params: withValues(properties as Record<string, unknown>),
    meta,
    timestamp: Date.now(),
  };

  // Push to GTM dataLayer immediately (not batched)
  pushToDataLayer(entry);

  const config = getConfig();
  if (!config) {
    if (preInitQueue.length >= MAX_QUEUE_SIZE) {
      preInitQueue.shift();
    }
    preInitQueue.push(entry);
    return;
  }

  sendEvent(entry);
}

export function flushQueue() {
  const queued = preInitQueue;
  preInitQueue = [];
  for (const entry of queued) {
    sendEvent(entry);
  }
}

/** @internal — test-only reset */
export function _resetQueue() {
  preInitQueue = [];
  for (const timer of retryTimers) {
    clearTimeout(timer);
  }
  retryTimers.clear();
}
